import React from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';

import { BodyText, DisplayText } from '../../Text';
import { theme } from '@garnish/constants';
import { useResponsive } from '../../../hooks';

// ────────────────────────────────────────────────────────────────────────────────

export const SectionHeader: React.FC<SectionHeaderProps> = (props) => {
  const { heading, children, variation = 'body', withoutBorder, style } = props;

  return (
    <Wrapper withoutBorder={withoutBorder} style={style}>
      <Title variation={variation}>{heading}</Title>
      <ChildrenWrapper>{children}</ChildrenWrapper>
    </Wrapper>
  );
};

// ─── SUBCOMPONENTS ──────────────────────────────────────────────────────────────

const Wrapper: React.FC<WrapperProps> = (props) => {
  const { withoutBorder, style, children } = props;
  const wrapperStyle = useWrapperStyle({ withoutBorder, style });

  return (
    <View style={wrapperStyle} accessibilityRole="header">
      {children}
    </View>
  );
};

const Title: React.FC<TitleProps> = (props) => {
  const { children, variation } = props;
  const { minWidth } = useResponsive();

  const titleFontSize = minWidth.tablet ? 3 : 2;

  if (variation === 'display')
    return <DisplayText size={titleFontSize}>{children}</DisplayText>;

  return <BodyText size={titleFontSize}>{children}</BodyText>;
};

const ChildrenWrapper: React.FC = (props) => {
  if (!checkIfHasChildren(props.children)) return null;

  return <View style={styles.childrenWrapper}>{props.children}</View>;
};

// ─── STYLES ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  wrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  wrapperWithBorder: {
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.GRAY,
    paddingBottom: 6,
  },

  childrenWrapper: {
    paddingLeft: theme.spacing.medium,
  },
});

const useWrapperStyle = (props: WrapperProps) => {
  const { withoutBorder, style } = props;

  const optionalBorderStyle = withoutBorder ? {} : styles.wrapperWithBorder;

  return [styles.wrapper, optionalBorderStyle, style];
};

// ─── UTILS ──────────────────────────────────────────────────────────────────────

function checkIfHasChildren(children: React.ReactNode) {
  return React.Children.count(children) > 0;
}

// ────────────────────────────────────────────────────────────────────────────────

type SectionHeaderProps = {
  readonly heading: string;
  readonly variation?: 'body' | 'display';
  readonly withoutBorder?: boolean;
  readonly style?: ViewProps['style'];
};

type WrapperProps = Pick<SectionHeaderProps, 'withoutBorder' | 'style'>;

type TitleProps = Pick<SectionHeaderProps, 'variation'>;
